import { normalizeDate, toMinute } from "@utils/date-time";
import { t, type TFunction } from "i18next";
import { z } from "zod";
import { FORM_FIELDS } from "../constants";

const LANG_BASE = "vessel-call.shipping-agency-transfer";
const MANDATORY_KEY = "form-validation.mandatory";

const requiredString = (label: string) => z.string().min(1, t(MANDATORY_KEY, { label }));

const requiredDate = (label: string) =>
  z.union([
    z.date({
      required_error: t(MANDATORY_KEY, { label }),
      invalid_type_error: t(MANDATORY_KEY, { label }),
    }),
    z.string().min(1, t(MANDATORY_KEY, { label })),
  ]);

export const refineRequiredDateTime =
  (label: string, messageKey: string) =>
  <T extends z.ZodType<Date | null | string>>(
    schema: T,
  ): z.ZodEffects<T, Date | null | string, Date | null | string> =>
    schema.refine(
      (val) => {
        const dMin = toMinute(val);
        if (!dMin) return true;
        const nowMin = toMinute(new Date())!;
        return dMin.getTime() >= nowMin.getTime();
      },
      { message: t(messageKey, { label }) },
    );

const requiredNotPastDateTime = (label: string) =>
  refineRequiredDateTime(label, "form-validation.must-not-be-in-past")(requiredDate(label));

const hasDateTimeValue = (value: Date | string | null | undefined): boolean => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
};

const toComparableDay = (value: Date | string | null | undefined): Date | null => {
  if (!hasDateTimeValue(value)) return null;
  return normalizeDate(value);
};

const additionalDocumentItemSchema = z
  .object({
    name: z.string(),
    url: z.string(),
    type: z.string(),
    documentType: z.string(),
    otherSpecifyType: z.string().max(
      30,
      t("form-validation.max-length", {
        label: t(`${LANG_BASE}.additional-documents.other-specify`),
        max: 30,
      }),
    ),
    documentName: z.string().max(
      50,
      t("form-validation.max-length", {
        label: t(`${LANG_BASE}.additional-documents.document-name`),
        max: 50,
      }),
    ),
    issueDate: z.union([z.date(), z.string(), z.null()]).optional(),
    expiryDate: z.union([z.date(), z.string(), z.null()]).optional(),
    documentSummary: z.string().max(
      300,
      t("form-validation.max-length", {
        label: t(`${LANG_BASE}.additional-documents.document-summary`),
        max: 300,
      }),
    ),
  })
  .superRefine((val, ctx) => {
    const issueDateLabel = t(`${LANG_BASE}.additional-documents.issue-date`);
    const expiryDateLabel = t(`${LANG_BASE}.additional-documents.expiry-date`);
    const todayDate = normalizeDate(new Date());

    if (val.documentType === "OTHER_SPECIFY" && !val.otherSpecifyType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.mandatory", {
          label: t(`${LANG_BASE}.additional-documents.other-specify`),
        }),
        path: ["otherSpecifyType"],
      });
    }
    if (val.documentType === "CERTIFICATE" && !val.expiryDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.mandatory", {
          label: t(`${LANG_BASE}.additional-documents.expiry-date`),
        }),
        path: ["expiryDate"],
      });
    }
    if (!val.documentName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.mandatory", {
          label: t(`${LANG_BASE}.additional-documents.document-name`),
        }),
        path: ["documentName"],
      });
    }
    if (!val.documentType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.mandatory", {
          label: t(`${LANG_BASE}.additional-documents.document-type`),
        }),
        path: ["documentType"],
      });
    }

    const issueDateDay = toComparableDay(val.issueDate);
    if (issueDateDay && todayDate && issueDateDay.getTime() > todayDate.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.must-not-be-in-future", {
          label: issueDateLabel,
        }),
        path: ["issueDate"],
      });
    }

    const expiryDateDay = toComparableDay(val.expiryDate);
    if (expiryDateDay && todayDate && expiryDateDay.getTime() < todayDate.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.must-not-be-in-past", {
          label: expiryDateLabel,
        }),
        path: ["expiryDate"],
      });
    }
  });

export const getRequestShippingAgencyTransferFormSchema = (t: TFunction) =>
  z.object({
    [FORM_FIELDS.NEW_SHIPPING_AGENCY]: requiredString(
      t(`${LANG_BASE}.form-fields.${FORM_FIELDS.NEW_SHIPPING_AGENCY}`),
    ),
    [FORM_FIELDS.TRANSFER_DATE_AND_TIME]: requiredNotPastDateTime(
      t(`${LANG_BASE}.form-fields.${FORM_FIELDS.TRANSFER_DATE_AND_TIME}`),
    ),
    [FORM_FIELDS.ADDITIONAL_DOCUMENTS]: z.array(additionalDocumentItemSchema).optional(),
  });

export type RequestShippingAgencyTransferFormData = z.infer<
  ReturnType<typeof getRequestShippingAgencyTransferFormSchema>
>;
